import { useMemo, useState } from 'react'
import type { CSSProperties, ReactElement } from 'react'
import type { GameViewProps } from '@games/core'
import type {
  BoardPresentation,
  Color,
  FogChessPlayerView,
  FogChessSpectatorView,
  FogChessView,
  Move,
  PieceType,
  SpectateMode,
} from './types'
import { LABELS } from './labels'
import { invertKeys } from './visibility'
import { Board } from './Board'

export type FogChessGameViewProps = GameViewProps<FogChessView, Move>

const colorName = (color: Color): string => (color === 'r' ? '红方' : '黑方')

// 本包刻意不依赖 Tailwind（保持游戏包与样式方案解耦），内联样式又写不了媒体查询，
// 所以响应式一律用 clamp()：小屏自动收紧、大屏保持原尺寸。
const chipStyle = (color: Color): CSSProperties => ({
  width: 'clamp(16px, 2.8vh, 24px)',
  height: 'clamp(16px, 2.8vh, 24px)',
  borderRadius: '50%',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 'clamp(10px, 1.7vh, 12px)',
  fontWeight: 'bold',
  border: '1.5px solid #333',
  background: color === 'r' ? '#fdf1d0' : '#f0f0f0',
  color: color === 'r' ? '#c0392b' : '#1a1a1a',
})

const bannerStyle = (variant: 'my' | 'opp' | 'over'): CSSProperties => {
  const base: CSSProperties = {
    padding: 'clamp(0.35rem, 2vw, 0.6rem) clamp(0.6rem, 3vw, 1rem)',
    borderRadius: 8,
    marginBottom: 'clamp(0.5rem, 2vh, 1rem)',
    fontWeight: 'bold',
    textAlign: 'center',
    fontSize: 'clamp(0.95rem, 3.6vw, 1.1rem)',
  }
  if (variant === 'my') {
    return { ...base, background: 'rgba(46, 204, 113, 0.25)', border: '2px solid #2ecc71', color: '#2ecc71' }
  }
  if (variant === 'over') {
    return { ...base, background: 'rgba(255, 213, 79, 0.15)', border: '2px solid rgba(255, 213, 79, 0.7)', color: '#ffd54f' }
  }
  return { ...base, background: 'rgba(255, 255, 255, 0.06)', border: '2px solid rgba(255, 255, 255, 0.15)', color: '#aaa' }
}

const captureRow = (
  label: string,
  chips: readonly PieceType[],
  chipColor: Color,
): ReactElement => (
  <div>
    {label}
    <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 4, verticalAlign: 'middle' }}>
      {chips.length === 0 ? (
        <span style={{ opacity: 0.5 }}>无</span>
      ) : (
        chips.map((type, index) => (
          <span key={`${type}-${index}`} style={chipStyle(chipColor)}>
            {LABELS[chipColor][type]}
          </span>
        ))
      )}
    </span>
  </div>
)

/** 观众视角的三种模式（D9）。 */
const MODE_LABELS: Readonly<Record<SpectateMode, string>> = {
  god: '上帝视角',
  red: '红方视角',
  black: '黑方视角',
}

const RED_MASK_COLOR = 'rgba(214, 48, 49, 0.5)'
const GREY_MASK_COLOR = 'rgba(44, 48, 54, 0.78)'

const modeButtonStyle = (active: boolean): CSSProperties => ({
  padding: 'clamp(0.3rem, 1.6vw, 0.45rem) clamp(0.5rem, 2.6vw, 0.85rem)',
  borderRadius: 8,
  fontSize: 'clamp(0.78rem, 2.8vw, 0.9rem)',
  fontWeight: active ? 'bold' : 'normal',
  cursor: 'pointer',
  color: active ? '#1a1a1a' : '#e8e8e8',
  background: active ? '#ffd54f' : 'rgba(255, 255, 255, 0.08)',
  border: active ? '1px solid #ffd54f' : '1px solid rgba(255, 255, 255, 0.25)',
})

const legendItemStyle = (): CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  whiteSpace: 'nowrap',
})

const legendSwatchStyle = (background: string): CSSProperties => ({
  display: 'inline-block',
  width: 16,
  height: 16,
  borderRadius: 3,
  background,
  border: '1px solid rgba(0, 0, 0, 0.4)',
})

/** 对局视图的最外层容器样式：两个分支（玩家 / 观众）共用。 */
const frameStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  flex: '1 1 auto',
  // 必须显式限制宽度：父级是 row flex，本节点默认 min-width:auto，会被棋盘那 492px 的
  // 固定宽度撑开 —— 于是棋盘量到的「可用宽度」永远偏大、格距锁死在 54px 上限，
  // 窄屏（手机）下直接横向溢出被裁切。加上 width:100% + minWidth:0 才能让它真正受容器约束。
  width: '100%',
  minWidth: 0,
  minHeight: 0,
}

const rowStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 'clamp(0.4rem, 2.5vw, 1rem)',
  alignItems: 'center',
  justifyContent: 'space-between',
}

/** 观众点棋盘不应产生任何出站消息；`disabled` 已经拦住了选中，这里只是与 props 形状对齐。 */
const ignoreMove = (): void => undefined

function PlayerLayout({
  view,
  disabled,
  onMove,
}: {
  readonly view: FogChessPlayerView
  readonly disabled: boolean
  readonly onMove: (move: Move) => void
}): ReactElement {
  const gameOver = view.winner !== null
  const myTurn = !gameOver && view.turn === view.self
  const opponent: Color = view.self === 'r' ? 'b' : 'r'

  const presentation = useMemo<BoardPresentation>(
    () => ({
      board: view.board,
      flip: view.flip,
      self: view.self,
      visible: new Set(view.visible),
      lastMove: view.lastMove,
      winner: view.winner,
      masks: [],
    }),
    [view],
  )

  return (
    <div style={frameStyle}>
      <div style={bannerStyle(gameOver ? 'over' : myTurn ? 'my' : 'opp')}>
        {gameOver && view.winner
          ? `${colorName(view.winner)} 获胜（将/帅被吃）。${view.winner === view.self ? '你赢了！' : '你输了。'}`
          : myTurn
            ? '轮到你走棋了！'
            : '等待对方走棋…'}
      </div>

      <div style={rowStyle}>
        <div>
          你的颜色：<strong>{colorName(view.self)}</strong>
        </div>
        <div>
          当前回合：
          <strong style={{ color: view.turn === 'r' ? '#e57373' : '#dddddd' }}>{colorName(view.turn)}</strong>
        </div>
      </div>

      <div style={{ marginTop: 'clamp(0.25rem, 1.5vh, 0.5rem)', fontSize: 'clamp(0.78rem, 2.6vh, 0.875rem)' }}>
        {captureRow('我方损失：', view.capturedMine, view.self)}
        {captureRow('对方损失：', view.capturedOpp, opponent)}
      </div>

      <Board
        presentation={presentation}
        disabled={disabled || view.disabled}
        onMove={(from, to) => onMove({ from, to })}
      />
    </div>
  )
}

function SpectatorLayout({ view }: { readonly view: FogChessSpectatorView }): ReactElement {
  const [mode, setMode] = useState<SpectateMode>('god')

  const presentation = useMemo<BoardPresentation>(() => {
    const hiddenFromRed = new Set(view.hiddenFromRed)
    const hiddenFromBlack = new Set(view.hiddenFromBlack)

    if (mode === 'red') {
      // 代入红方：红方看不到的格子变迷雾，其余揭示；红方本就在下方，不翻转
      return {
        board: view.board,
        flip: false,
        self: null,
        visible: invertKeys(hiddenFromRed),
        lastMove: view.lastMove,
        winner: view.winner,
        masks: [],
      }
    }
    if (mode === 'black') {
      // 代入黑方：翻转 180°，让黑方棋子在下 —— 与黑方玩家本人看到的画面一致
      return {
        board: view.board,
        flip: true,
        self: null,
        visible: invertKeys(hiddenFromBlack),
        lastMove: view.lastMove,
        winner: view.winner,
        masks: [],
      }
    }
    // 上帝视角（D1）：完整棋盘 + 双方「看不到」的格子各盖一层（D2/D8）
    return {
      board: view.board,
      flip: false,
      self: null,
      visible: null,
      lastMove: view.lastMove,
      winner: view.winner,
      masks: [
        { color: 'r', cells: hiddenFromRed },
        { color: 'b', cells: hiddenFromBlack },
      ],
    }
  }, [view, mode])

  const gameOver = view.winner !== null

  return (
    <div style={frameStyle}>
      <div style={bannerStyle(gameOver ? 'over' : 'opp')}>
        {gameOver && view.winner
          ? `${colorName(view.winner)} 获胜（将/帅被吃）。`
          : `观战中 · 等待${colorName(view.turn)}走棋…`}
      </div>

      <div style={rowStyle}>
        <div>
          你正在观战：<strong>{MODE_LABELS[mode]}</strong>
        </div>
        <div>
          当前回合：
          <strong style={{ color: view.turn === 'r' ? '#e57373' : '#dddddd' }}>{colorName(view.turn)}</strong>
        </div>
      </div>

      <div
        role="group"
        aria-label="观察视角"
        style={{ display: 'flex', flexWrap: 'wrap', gap: 'clamp(0.3rem, 1.5vw, 0.5rem)', marginTop: 'clamp(0.3rem, 1.5vh, 0.6rem)' }}
      >
        {(['god', 'red', 'black'] as const).map((option) => (
          <button
            key={option}
            type="button"
            aria-pressed={mode === option}
            style={modeButtonStyle(mode === option)}
            onClick={() => setMode(option)}
          >
            {MODE_LABELS[option]}
          </button>
        ))}
      </div>

      {mode === 'god' ? (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 'clamp(0.4rem, 2vw, 0.8rem)',
            alignItems: 'center',
            marginTop: 'clamp(0.3rem, 1.2vh, 0.5rem)',
            fontSize: 'clamp(0.72rem, 2.5vw, 0.82rem)',
            opacity: 0.9,
          }}
        >
          <span style={legendItemStyle()}>
            <span style={legendSwatchStyle(RED_MASK_COLOR)} />红＝红方看不到
          </span>
          <span style={legendItemStyle()}>
            <span style={legendSwatchStyle(GREY_MASK_COLOR)} />灰＝黑方看不到
          </span>
          <span
            style={legendItemStyle()}
          >
            <span
              style={legendSwatchStyle(
                `linear-gradient(${RED_MASK_COLOR}, ${RED_MASK_COLOR}), ${GREY_MASK_COLOR}`,
              )}
            />
            红灰都有＝双方都看不到
          </span>
        </div>
      ) : null}

      <Board presentation={presentation} disabled onMove={ignoreMove} />
    </div>
  )
}

/** 战争迷雾象棋的对局视图：玩家视角与观众视角共用同一个组件入口。 */
export function FogChessGameView({ view, disabled, onMove }: FogChessGameViewProps): ReactElement {
  if (view.kind === 'spectator') return <SpectatorLayout view={view} />
  return <PlayerLayout view={view} disabled={disabled} onMove={onMove} />
}